import axios from 'axios'
import Cookies from 'js-cookie'

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

export default {
  namespaced: true,
  
  state: {
    messages: [],
    loading: false,
    error: null,
    activeFilter: 'recent',
    pagination: {
      page: 1,
      pageSize: 15,
      pageCount: 0,
      total: 0
    },
    userInteractions: {
      likes: {},
      dislikes: {}
    }
  },
  
  getters: {
    allMessages: state => state.messages,
    isLoading: state => state.loading,
    error: state => state.error,
    pagination: state => state.pagination,
    userInteractions: state => state.userInteractions,
    activeFilter: state => state.activeFilter,
    hasInteracted: state => (type, id) => {
      return state.userInteractions[type][id] === true
    }
  },
  
  actions: {
    async fetchLatestMessages({ commit, state }) {
      commit('SET_LOADING', true)
      try {
        const response = await api.get('/api/chat-globals/latest', {
          params: { type: state.activeFilter },
          headers: { 
            Authorization: `Bearer ${Cookies.get('jwt')}` 
          }
        })
        const messages = response.data;
        console.log('Fetched messages:', messages)
        commit('SET_MESSAGES', messages)
        
        // Sincronizar com o estado local
        if (messages && messages.length > 0) {
          messages.forEach(message => {
            if (message.userInteraction?.hasLiked) {
              commit('SET_INTERACTION', { type: 'likes', id: message.id, value: true });
            }
            if (message.userInteraction?.hasDisliked) {
              commit('SET_INTERACTION', { type: 'dislikes', id: message.id, value: true });
            }
          });
        }
        
        commit('SET_ERROR', null)
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to fetch messages')
        console.error('Error fetching messages:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchPaginatedMessages({ commit, state }, page = 1) {
      commit('SET_LOADING', true)
      try {
        const response = await api.get('/api/chat-globals/paginated', {
          params: { 
            page, 
            pageSize: 15,
            type: state.activeFilter 
          },
          headers: { 
            Authorization: `Bearer ${Cookies.get('jwt')}` 
          }
        })
        commit('SET_MESSAGES', response.data.data)
        commit('SET_PAGINATION', response.data.meta.pagination)
        commit('SET_ERROR', null)
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to fetch messages')
        console.error('Error fetching paginated messages:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createMessage({ commit, dispatch }, text) {
      commit('SET_LOADING', true)
      try {
        await api.post('/api/chat-globals/create', 
          { text }, 
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        )
        await dispatch('fetchLatestMessages')
        return true
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to create message')
        console.error('Error creating message:', error)
        return false
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteMessage({ commit, dispatch }, id) {
      commit('SET_LOADING', true)
      try {
        await api.delete(`/api/chat-globals/${id}`, {
          headers: { 
            Authorization: `Bearer ${Cookies.get('jwt')}` 
          }
        })
        await dispatch('fetchLatestMessages')
        return true
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to delete message')
        console.error('Error deleting message:', error)
        return false
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async editMessage({ commit, dispatch }, { id, text }) {
      commit('SET_LOADING', true)
      try {
        await api.put(`/api/chat-globals/${id}`, 
          { text },
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        )
        await dispatch('fetchLatestMessages')
        return true
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to edit message')
        console.error('Error editing message:', error)
        return false
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async addLike({ commit, state }, id) {
      try {
        // Verificar estado atual
        const currentlyLiked = state.userInteractions.likes[id];
        const currentlyDisliked = state.userInteractions.dislikes[id];
        
        // Se já tem like, não faz nada
        if (currentlyLiked) {
          return true;
        }
        
        // Encontrar a mensagem atual para fazer atualização local
        const messageIndex = state.messages.findIndex(msg => msg.id === id);
        if (messageIndex === -1) {
          console.error(`Mensagem com ID ${id} não encontrada`);
          return false;
        }
        
        // Incrementar o contador de likes localmente
        const updatedMessage = { 
          ...state.messages[messageIndex],
          likes: (state.messages[messageIndex].likes || 0) + 1
        };
        
        // Se estava com dislike, decrementar o contador de dislikes localmente
        if (currentlyDisliked) {
          updatedMessage.dislikes = Math.max(0, (updatedMessage.dislikes || 0) - 1);
          commit('SET_INTERACTION', { type: 'dislikes', id, value: false });
        }
        
        // Atualizar a mensagem no estado local imediatamente para feedback visual
        commit('UPDATE_MESSAGE', { id, data: updatedMessage });
        
        // Atualizar UI para mostrar que o usuário deu like
        commit('SET_INTERACTION', { type: 'likes', id, value: true });
        
        // Se tem dislike, remover primeiro no backend (e aguardar)
        if (currentlyDisliked) {
          try {
            await api.delete(`/api/chat-globals/${id}/dislike`, {
              headers: { 
                Authorization: `Bearer ${Cookies.get('jwt')}` 
              }
            });
            console.log('Dislike removido com sucesso');
          } catch (error) {
            console.warn('Erro ao remover dislike, continuando com like:', error);
            // Continua mesmo com erro
          }
        }
        
        // Adicionar like no backend
        try {
          const response = await api.post(`/api/chat-globals/${id}/like`, {}, {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          });
          
          // Atualizar mensagem com dados do servidor para garantir precisão
          commit('UPDATE_MESSAGE', { id, data: response.data });
          return true;
        } catch (error) {
          // Se falhar, reverter UI e contadores
          if (currentlyDisliked) {
            commit('SET_INTERACTION', { type: 'dislikes', id, value: true });
            
            // Restaurar contadores originais
            const originalMessage = { 
              ...state.messages[messageIndex],
              likes: Math.max(0, (updatedMessage.likes || 1) - 1),
              dislikes: (state.messages[messageIndex].dislikes || 0)
            };
            commit('UPDATE_MESSAGE', { id, data: originalMessage });
          } else {
            // Apenas reverter o contador de likes
            commit('UPDATE_MESSAGE', { 
              id, 
              data: { 
                likes: state.messages[messageIndex].likes - 1 
              } 
            });
          }
          
          commit('SET_INTERACTION', { type: 'likes', id, value: false });
          throw error;
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to like message');
        console.error('Error liking message:', error);
        return false;
      }
    },
    
    async removeLike({ commit, state }, id) {
      try {
        // Verificar se realmente tem like
        if (!state.userInteractions.likes[id]) {
          console.log('Like já removido, ignorando requisição');
          return true;
        }
        
        // Encontrar a mensagem atual para fazer atualização local
        const messageIndex = state.messages.findIndex(msg => msg.id === id);
        if (messageIndex === -1) {
          console.error(`Mensagem com ID ${id} não encontrada`);
          return false;
        }
        
        // Atualizar contador localmente para feedback imediato
        const updatedMessage = { 
          ...state.messages[messageIndex],
          likes: Math.max(0, (state.messages[messageIndex].likes || 1) - 1)
        };
        commit('UPDATE_MESSAGE', { id, data: updatedMessage });
        
        // Atualizar UI antecipadamente
        commit('SET_INTERACTION', { type: 'likes', id, value: false });
        
        try {
          const response = await api.delete(`/api/chat-globals/${id}/like`, {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          });
          
          // Atualizar mensagem com dados do servidor
          commit('UPDATE_MESSAGE', { id, data: response.data });
          return true;
        } catch (error) {
          // Se falhar, reverter UI e contadores
          commit('SET_INTERACTION', { type: 'likes', id, value: true });
          
          // Restaurar contador original
          const originalMessage = { 
            ...state.messages[messageIndex],
            likes: (state.messages[messageIndex].likes || 0) + 1
          };
          commit('UPDATE_MESSAGE', { id, data: originalMessage });
          
          throw error;
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to remove like');
        console.error('Error removing like:', error);
        return false;
      }
    },
    
    async addDislike({ commit, state }, id) {
      try {
        // Verificar estado atual
        const currentlyLiked = state.userInteractions.likes[id];
        const currentlyDisliked = state.userInteractions.dislikes[id];
        
        // Se já tem dislike, não faz nada
        if (currentlyDisliked) {
          return true;
        }
        
        // Encontrar a mensagem atual para fazer atualização local
        const messageIndex = state.messages.findIndex(msg => msg.id === id);
        if (messageIndex === -1) {
          console.error(`Mensagem com ID ${id} não encontrada`);
          return false;
        }
        
        // Incrementar o contador de dislikes localmente
        const updatedMessage = { 
          ...state.messages[messageIndex],
          dislikes: (state.messages[messageIndex].dislikes || 0) + 1
        };
        
        // Se estava com like, decrementar o contador de likes localmente
        if (currentlyLiked) {
          updatedMessage.likes = Math.max(0, (updatedMessage.likes || 0) - 1);
          commit('SET_INTERACTION', { type: 'likes', id, value: false });
        }
        
        // Atualizar a mensagem no estado local imediatamente para feedback visual
        commit('UPDATE_MESSAGE', { id, data: updatedMessage });
        
        // Atualizar UI para mostrar que o usuário deu dislike
        commit('SET_INTERACTION', { type: 'dislikes', id, value: true });
        
        // Se tem like, remover primeiro no backend (e aguardar)
        if (currentlyLiked) {
          try {
            await api.delete(`/api/chat-globals/${id}/like`, {
              headers: { 
                Authorization: `Bearer ${Cookies.get('jwt')}` 
              }
            });
            console.log('Like removido com sucesso');
          } catch (error) {
            console.warn('Erro ao remover like, continuando com dislike:', error);
            // Continua mesmo com erro
          }
        }
        
        // Adicionar dislike no backend
        try {
          const response = await api.post(`/api/chat-globals/${id}/dislike`, {}, {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          });
          
          // Atualizar mensagem com dados do servidor para garantir precisão
          commit('UPDATE_MESSAGE', { id, data: response.data });
          return true;
        } catch (error) {
          // Se falhar, reverter UI e contadores
          if (currentlyLiked) {
            commit('SET_INTERACTION', { type: 'likes', id, value: true });
            
            // Restaurar contadores originais
            const originalMessage = { 
              ...state.messages[messageIndex],
              dislikes: Math.max(0, (updatedMessage.dislikes || 1) - 1),
              likes: (state.messages[messageIndex].likes || 0)
            };
            commit('UPDATE_MESSAGE', { id, data: originalMessage });
          } else {
            // Apenas reverter o contador de dislikes
            commit('UPDATE_MESSAGE', { 
              id, 
              data: { 
                dislikes: state.messages[messageIndex].dislikes - 1 
              } 
            });
          }
          
          commit('SET_INTERACTION', { type: 'dislikes', id, value: false });
          throw error;
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to dislike message');
        console.error('Error disliking message:', error);
        return false;
      }
    },
    
    async removeDislike({ commit, state }, id) {
      try {
        // Verificar se realmente tem dislike
        if (!state.userInteractions.dislikes[id]) {
          console.log('Dislike já removido, ignorando requisição');
          return true;
        }
        
        // Encontrar a mensagem atual para fazer atualização local
        const messageIndex = state.messages.findIndex(msg => msg.id === id);
        if (messageIndex === -1) {
          console.error(`Mensagem com ID ${id} não encontrada`);
          return false;
        }
        
        // Atualizar contador localmente para feedback imediato
        const updatedMessage = { 
          ...state.messages[messageIndex],
          dislikes: Math.max(0, (state.messages[messageIndex].dislikes || 1) - 1)
        };
        commit('UPDATE_MESSAGE', { id, data: updatedMessage });
        
        // Atualizar UI antecipadamente
        commit('SET_INTERACTION', { type: 'dislikes', id, value: false });
        
        try {
          const response = await api.delete(`/api/chat-globals/${id}/dislike`, {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          });
          
          // Atualizar mensagem com dados do servidor
          commit('UPDATE_MESSAGE', { id, data: response.data });
          return true;
        } catch (error) {
          // Se falhar, reverter UI e contadores
          commit('SET_INTERACTION', { type: 'dislikes', id, value: true });
          
          // Restaurar contador original
          const originalMessage = { 
            ...state.messages[messageIndex],
            dislikes: (state.messages[messageIndex].dislikes || 0) + 1
          };
          commit('UPDATE_MESSAGE', { id, data: originalMessage });
          
          throw error;
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to remove dislike');
        console.error('Error removing dislike:', error);
        return false;
      }
    },
    
    async sendCoins({ commit }, { id, amount }) {
      try {
        const response = await api.post(`/api/chat-globals/${id}/send-coins`, 
          { amount },
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        )
        commit('UPDATE_MESSAGE', { id, data: response.data })
        return true
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.error?.message || error.message || 'Failed to send coins')
        console.error('Error sending coins:', error)
        return false
      }
    },

    setActiveFilter({ commit, dispatch }, filterType) {
      commit('SET_FILTER', filterType)
      dispatch('fetchLatestMessages')
    }
  },
  
  mutations: {
    SET_MESSAGES(state, messages) {
      state.messages = messages
    },
    
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    
    SET_ERROR(state, error) {
      state.error = error
    },
    
    SET_PAGINATION(state, pagination) {
      state.pagination = pagination
    },
    
    UPDATE_MESSAGE(state, { id, data }) {
      const index = state.messages.findIndex(msg => msg.id === id)
      if (index !== -1) {
        state.messages[index] = { ...state.messages[index], ...data }
      }
    },
    
    SET_INTERACTION(state, { type, id, value }) {
      state.userInteractions[type] = {
        ...state.userInteractions[type],
        [id]: value
      }
    },

    SET_FILTER(state, filterType) {
      state.activeFilter = filterType
      state.pagination.page = 1
    }
  }
}
